import { RefObject, useEffect, useRef, useState } from 'react';

const MAX_FILTERS_WIDTH = 970;
// Вернуться в одну строку — по более высокому порогу, чтобы вёрстка не моргала.
const STACK_THRESHOLD = 500;
const UNSTACK_THRESHOLD = 560;

interface IFilter {
  id: string;
}

interface IUseIsNarrowFiltersResult {
  isNarrow: boolean;
  shouldStackVertically: boolean;
}

const measureGroupContentWidth = (group: HTMLElement): number => {
  const children = Array.from(group.children) as HTMLElement[];

  if (children.length === 0) {
    return 0;
  }

  const first = children[0].getBoundingClientRect();
  const last = children[children.length - 1].getBoundingClientRect();

  return last.right - first.left;
};

export const useIsNarrowFilters = (
  filtersRef: RefObject<HTMLDivElement | null>,
  filters: IFilter[],
): IUseIsNarrowFiltersResult => {
  const [isNarrow, setIsNarrow] = useState(false);
  const [shouldStackVertically, setShouldStackVertically] = useState(false);
  const stackRef = useRef(false);

  stackRef.current = shouldStackVertically;

  useEffect(() => {
    const filtersElement = filtersRef.current;

    if (!filtersElement) {
      return;
    }

    const recalculate = () => {
      const groups = Array.from(filtersElement.children) as HTMLElement[];

      if (groups.length < 2) {
        setIsNarrow(false);
        setShouldStackVertically(false);

        return;
      }

      const [firstGroup, secondGroup] = groups;
      const parentWidth = filtersElement.getBoundingClientRect().width;
      const marginLeft = parseInt(getComputedStyle(secondGroup).marginLeft, 10) || 0;

      const firstGroupContentWidth = measureGroupContentWidth(firstGroup);
      const secondGroupContentWidth = measureGroupContentWidth(secondGroup);

      const totalContentWidth = firstGroupContentWidth + secondGroupContentWidth + marginLeft;
      setIsNarrow(totalContentWidth > MAX_FILTERS_WIDTH);

      const availableForSecondGroup = parentWidth - firstGroupContentWidth - marginLeft;
      const threshold = stackRef.current ? UNSTACK_THRESHOLD : STACK_THRESHOLD;
      setShouldStackVertically(availableForSecondGroup < threshold);
    };

    recalculate();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(recalculate);

    observer.observe(filtersElement);

    return () => observer.disconnect();
  }, [filtersRef, filters]);

  return { isNarrow, shouldStackVertically };
};
